import React, { useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";

function AnimatedElectronTransfer({
  from = [-2, 0, 0],
  to = [2, 0, 0],
  start = false,
  duration = 1.1
}) {
  const meshRef = useRef();
  useFrame(({ clock }) => {
    if (meshRef.current && start) {
      // Animate electron from 'from' to 'to'
      const t = Math.min(clock.getElapsedTime() / duration, 1);
      // Simple arc (parabola) for effect
      const x = from[0] + (to[0] - from[0]) * t;
      const y = from[1] + (to[1] - from[1]) * t + Math.sin(Math.PI * t) * 0.7; // arc up
      const z = from[2] + (to[2] - from[2]) * t;
      meshRef.current.position.set(x, y, z);
      // Optionally fade out near end
      meshRef.current.material.opacity = 1 - t * 0.2;
      if (t >= 1) meshRef.current.visible = false;
    }
  });
  return (
    <mesh ref={meshRef} position={from}>
      <sphereGeometry args={[0.14, 22, 22]} />
      <meshStandardMaterial color="#e6b333" emissive="#fff6aa" opacity={0.95} transparent />
    </mesh>
  );
}

function MiniNucleus({ protons = 6, neutrons = 6 }) {
  const particles = [];
  const total = protons + neutrons;
  for (let i = 0; i < total; i++) {
    const angle = Math.random() * 2 * Math.PI;
    const z = (Math.random() - 0.5) * 0.35;
    const r = 0.20 + Math.random() * 0.13;
    const x = r * Math.cos(angle);
    const y = r * Math.sin(angle);
    const color = i < protons ? "#e74c3c" : "#90caf9";
    particles.push(
      <mesh key={i} position={[x, y, z]}>
        <sphereGeometry args={[0.20, 20, 20]} />
        <meshStandardMaterial
          color={color}
          metalness={0.16}
          roughness={0.27}
          emissive={i < protons ? "#c0392b" : "#bbdefb"}
          emissiveIntensity={i < protons ? 0.08 : 0.09}
        />
      </mesh>
    );
  }
  return <>{particles}</>;
}

// Re-usable 3D Bohr model for one atom (no animation)
function SimpleBohrModel({ element, shellConfig, position = [0,0,0], highlight = false }) {
  const baseRadius = 1.06;
  const shellGap = 0.36;
  const shells = shellConfig || element.shellConfig || [];
  const protons = element.protons;
  const neutrons = Array.isArray(element.neutrons)
    ? element.neutrons[0]
    : (element.neutrons || element.protons);

  return (
    <group position={position}>
      <MiniNucleus protons={protons} neutrons={neutrons} />
      {shells.map((n, idx) => (
        <group key={idx}>
          {/* Shell ring */}
          <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, idx * shellGap, 0]}>
            <torusGeometry args={[baseRadius + idx * 0.30, 0.009, 16, 60]} />
            <meshStandardMaterial color={highlight ? "#e6b333" : "#b6c7e6"} opacity={0.84} transparent />
          </mesh>
          {/* Electrons */}
          {Array(n).fill(0).map((_, i) => {
            const angle = (2 * Math.PI * i) / n;
            return (
              <mesh
                key={i}
                position={[
                  (baseRadius + idx * 0.30) * Math.cos(angle),
                  idx * shellGap,
                  (baseRadius + idx * 0.30) * Math.sin(angle)
                ]}
              >
                <sphereGeometry args={[0.12, 20, 20]} />
                <meshStandardMaterial color="#3498db" emissive="#d6eaff" emissiveIntensity={0.08} />
              </mesh>
            );
          })}
        </group>
      ))}
    </group>
  );
}

// MAIN VISUALIZER: two atoms and electron transfer
export default function CompoundBohrVisualizer({
  donor,         // element object (e.g., Na)
  acceptor,      // element object (e.g., Cl)
  showTransfer,  // boolean, when true animates electron
  donorLossShellConfig, // donor's shellConfig after loss
  acceptorGainShellConfig // acceptor's shellConfig after gain
}) {
    // Defensive checks to avoid crashes
  if (
    !donor || !acceptor ||
    !donor.shellConfig || !acceptor.shellConfig
  ) {
    return <div style={{ color: "#b44", padding: "2em", textAlign: "center" }}>
      Loading atom data...
    </div>;
  }
  // Visual positions
  const donorPos = [-2, 0, 0];
  const acceptorPos = [2, 0, 0];
  // Start electron transfer from donor's outermost shell
  const donorShellRadius = 1.06 + (donor.shellConfig.length - 1) * 0.30;
  const fromPos = [
    donorPos[0] + donorShellRadius,
    donorPos[1],
    donorPos[2]
  ];
  // To acceptor's outermost shell
  const acceptorShellRadius = 1.06 + (acceptor.shellConfig.length - 1) * 0.30;
  const toPos = [
    acceptorPos[0] + acceptorShellRadius * Math.cos(Math.PI / 6),
    acceptorPos[1] + 0.22,
    acceptorPos[2] + acceptorShellRadius * Math.sin(Math.PI / 6)
  ];

  return (
    <div style={{ width: 460, height: 350, margin: "0 auto" }}>
      <Canvas camera={{ position: [0, 0, 7.5] }}>
        <ambientLight intensity={0.8} />
        <pointLight position={[0, 6, 4]} intensity={0.8} />
        {/* Donor atom before/after electron transfer */}
        <SimpleBohrModel
          element={donor}
          shellConfig={showTransfer ? donorLossShellConfig : donor.shellConfig}
          position={donorPos}
          highlight={!showTransfer}
        />
        {/* Acceptor atom before/after electron transfer */}
        <SimpleBohrModel
          element={acceptor}
          shellConfig={showTransfer ? acceptorGainShellConfig : acceptor.shellConfig}
          position={acceptorPos}
          highlight={showTransfer}
        />
        {/* Animated electron in transit */}
        {showTransfer && (
          <AnimatedElectronTransfer from={fromPos} to={toPos} start={true} />
        )}
        <OrbitControls />
      </Canvas>
    </div>
  );
}
